import React, { useState, useEffect } from 'react';
import { Textarea } from '../../ui/textarea';
import { useLazyCommand } from '@soap/modules';
import { Loader2, Edit2 } from 'lucide-react';

export type TopicInlineEditTarget = 'student' | 'tenant' | 'none';

interface InlineTextFieldEditorProps {
  value: string | null | undefined;
  topicId: string;
  studentId: string | null;
  /** Student plan (`C299`), tenant library (`C320`), or read-only when viewing from the student's curriculum tree (`none`). */
  editTarget?: TopicInlineEditTarget;
  fieldName: string;
  label: string;
  onUpdate?: (newValue: string | null) => void;
  rows?: number;
}

export function InlineTextFieldEditor({
  value,
  topicId,
  studentId,
  editTarget = 'student',
  fieldName,
  label,
  onUpdate,
  rows = 3
}: InlineTextFieldEditorProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [updateStudentCatalogTopicFields] = useLazyCommand("C299v1_UpdateCustomTopicFields");
  const [updateTenantCatalogTopicFields] = useLazyCommand("C320v1_UpdateTenantCustomCatalogTopicFields");

  const canEdit =
    editTarget !== 'none' &&
    !!topicId &&
    (editTarget === 'tenant' || (!!studentId && editTarget === 'student'));

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = async () => {
    if (!topicId || !canEdit) {
      setEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      if (editTarget === 'tenant') {
        const updatePayload: Record<string, unknown> = {
          $type: "C320v1_UpdateTenantCustomCatalogTopicFields",
          C320_TopicId: topicId
        };
        updatePayload[`C320_${fieldName}`] = editValue.trim() || null;
        await updateTenantCatalogTopicFields(updatePayload);
      } else if (editTarget === 'student' && studentId) {
        const updatePayload: Record<string, unknown> = {
          $type: "C299v1_UpdateCustomTopicFields",
          C299_StudentId: studentId,
          C299_TopicId: topicId
        };
        updatePayload[`C299_${fieldName}`] = editValue.trim() || null;
        await updateStudentCatalogTopicFields(updatePayload);
      } else {
        setEditing(false);
        return;
      }

      if (onUpdate) {
        onUpdate(editValue.trim() || null);
      }

      setEditing(false);
    } catch (error) {
      console.error(`Failed to update topic ${fieldName}:`, error);
      setEditValue(value || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          rows={rows}
          className="text-sm"
          autoFocus
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Press Ctrl+Enter to save, Esc to cancel</span>
          {isSaving && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
      </div>
    );
  }

  if (!value || value.trim() === '') {
    if (!canEdit) {
      return <span className="text-sm text-gray-500">—</span>;
    }
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-600 transition-colors group"
        disabled={isSaving}
        title={`Click to add ${label.toLowerCase()}`}
      >
        <span>Add {label}</span>
        <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
      </button>
    );
  }

  return (
    <div className="group">
      <div className="flex items-start justify-between gap-2">
        <p className="text-gray-700 leading-relaxed flex-1 whitespace-pre-wrap break-words">
          {value}
        </p>
        {canEdit && (
          <button
            onClick={() => setEditing(true)}
            className="flex-shrink-0 p-1 hover:bg-action-blue/10 rounded transition-colors opacity-0 group-hover:opacity-100"
            disabled={isSaving}
            title={`Click to edit ${label.toLowerCase()}`}
          >
            <Edit2 className="h-4 w-4 text-gray-400 hover:text-action-blue transition-colors" />
          </button>
        )}
      </div>
    </div>
  );
}
